import React, { useEffect, useState } from 'react'

import { Box, Divider, Stack, Typography, useTheme } from '@mui/material'

import { Button } from '@repo/ui'
import { KeyChip, SettingsSection } from 'components/display'
import { useShortcutBindings } from 'hooks'
import {
  FIXED_SHORTCUTS,
  REBINDABLE_SHORTCUTS,
  captureKeydownToBinding,
  findBindingConflict,
  formatBinding,
  isChord,
  resetShortcutBinding,
  saveShortcutBinding,
  type RebindableShortcutKey,
} from 'utils'

/**
 * Rebind keyboard shortcuts. Ported from Vergence's `controls-shortcuts-card`.
 *
 * Lives on Settings → Personal → **Controls**, its own route, matching Vergence's
 * `/settings/controls`. It briefly sat as a card on General beside Theme, on the
 * argument that both are per-device preferences. That was wrong for one concrete
 * reason: the `?` dialog links here, and a link into the middle of a long General
 * page lands the user somewhere they still have to hunt. A route lands them on the
 * thing they clicked for. Theme stays on General — it is an appearance preference,
 * not a control.
 *
 * ## Editing lives here; the `?` dialog is read-only
 *
 * That split is the point of the port. `KeyboardShortcutsDialog` is opened mid-task
 * to answer "what was that key again" and must stay glanceable — recording chrome
 * there would turn a two-second lookup into a modal with state. Both surfaces read
 * `SHORTCUT_CATALOG`, so neither can invent a label the other does not have.
 *
 * ## Both surfaces, one list, no drift
 *
 * Rows are generated from the catalog, not written out: `REBINDABLE_SHORTCUTS` get
 * Change / Reset, `FIXED_SHORTCUTS` render read-only under "Built-in". Adding a
 * shortcut is one catalog entry and it appears in both places.
 */

/** Copy shown while a row is capturing, and as the fallback validation message. */
const CHORD_REQUIREMENT = 'Hold ⌘, ⌥, or ⇧ and press another key. Esc cancels.'

export const ShortcutsCard = () => {
  const theme = useTheme()
  // Live rather than a snapshot: the same store is written by this card and read by
  // the sidebar rows, so both move together.
  const bindings = useShortcutBindings()
  const [recordingKey, setRecordingKey] = useState<RebindableShortcutKey | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!recordingKey) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setRecordingKey(null)
        setError(null)
        return
      }

      const candidate = captureKeydownToBinding(event)
      // A lone modifier while the user is still reaching for the second key. Not an
      // error, not a binding, so wait for the next keydown rather than rejecting.
      if (!candidate) return

      // Swallow the keystroke unconditionally once past that point. Without it,
      // recording ⌘B would ALSO toggle the sidebar, and recording ? would open the
      // shortcuts dialog on top of this page. Capture phase plus stopPropagation is
      // what gets in front of those window-level listeners.
      event.preventDefault()
      event.stopPropagation()

      if (!isChord(candidate)) {
        // A bare letter would fire while the user was simply reading a page, so it
        // is refused rather than stored.
        setError(CHORD_REQUIREMENT)
        return
      }

      const conflict = findBindingConflict(candidate, recordingKey, bindings)
      if (conflict) {
        // Refused, not merely warned. Two actions on one binding means whichever
        // handler registered first wins and the other silently stops working, with
        // nothing on screen to explain why.
        setError(`${formatBinding(candidate)} is already used by ${conflict.label}. Try another key.`)
        return
      }

      saveShortcutBinding(recordingKey, candidate)
      setRecordingKey(null)
      setError(null)
    }

    window.addEventListener('keydown', handleKeyDown, true)
    return () => window.removeEventListener('keydown', handleKeyDown, true)
  }, [recordingKey, bindings])

  const toggleRecording = (key: RebindableShortcutKey) => {
    setError(null)
    setRecordingKey(current => (current === key ? null : key))
  }

  const handleReset = (key: RebindableShortcutKey) => {
    if (recordingKey === key) setRecordingKey(null)
    setError(null)
    resetShortcutBinding(key)
  }

  return (
    <SettingsSection
      title="Keyboard shortcuts"
      description="Saved in this browser on this device. They do not follow you to your other devices. Press ? anywhere in Vera to see the full list."
    >
      <Stack spacing={4}>

        {REBINDABLE_SHORTCUTS.map(entry => {
          const isRecording = recordingKey === entry.key

          return (
            <Stack
              key={entry.key}
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={4}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" spacing={3}>
                  <Typography variant="body3">{entry.label}</Typography>
                  <KeyChip>{formatBinding(bindings[entry.key])}</KeyChip>
                </Stack>
                <Typography
                  variant="body4"
                  color="text.secondary"
                  // 1.5 so a two-line description does not read dense (§15).
                  sx={{ display: 'block', mt: 2, lineHeight: 1.5 }}
                >
                  {entry.description}
                </Typography>
                {isRecording && (
                  <Typography
                    variant="body4"
                    // The prompt is brand-tinted, the refusal is error-tinted: the
                    // row can be in either state and the colour is what distinguishes
                    // "go ahead" from "that one is taken" at a glance.
                    sx={{ display: 'block', mt: 2, color: error ? theme.palette.error.main : theme.palette.brand[600] }}
                  >
                    {error ?? CHORD_REQUIREMENT}
                  </Typography>
                )}
              </Box>

              {/* `tertiary` at default size, labelled "Change" — the house pattern
                  for a row-level action inside a settings card that reveals an
                  editor. It is what the Login rows on Personal → General use for
                  Email and Password, what ProfilePage uses for the same two, and
                  what CompanyGeneralPage / InstallationPage / FieldMappingEditor
                  use for their row actions (10 sites, no competing convention).

                  The ellipsis is dropped for the same reason. It is a real signal
                  ("more input needed"), but Login's Change opens a sub-form that
                  needs exactly as much further input and omits it — so keeping it
                  here would make two identical interactions on one page look like
                  two different kinds of control. Consistency wins over a nicety the
                  house has already declined.

                  `primary` while recording stays: that is a STATE, not a second
                  variant. It is the affordance saying "I am listening now", and
                  losing it would make an armed row indistinguishable from an idle
                  one apart from its label.

                  12px between the buttons of a pair (§29, 4px theme base). */}
              <Stack direction="row" spacing={3} sx={{ flexShrink: 0 }}>
                <Button
                  disableElevation
                  type="button"
                  variant={isRecording ? 'primary' : 'tertiary'}
                  onClick={() => toggleRecording(entry.key)}
                >
                  {isRecording ? 'Press shortcut…' : 'Change'}
                </Button>
                <Button
                  disableElevation
                  type="button"
                  variant="tertiary"
                  onClick={() => handleReset(entry.key)}
                >
                  Reset
                </Button>
              </Stack>
            </Stack>
          )
        })}

        {/* Built-in: bound by hardcoded handlers, so there is nothing to record.
            Listed anyway because discoverability is the whole reason the catalog
            includes them, and hiding them here would make this card disagree with
            the ? dialog about what shortcuts exist. */}
        <Divider />
        <Typography variant="body4" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Built-in
        </Typography>
        {FIXED_SHORTCUTS.map(entry => (
          <Stack
            key={entry.key}
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={4}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body3">{entry.label}</Typography>
              <Typography variant="body4" color="text.secondary" sx={{ display: 'block', mt: 2, lineHeight: 1.5 }}>
                {entry.description}
              </Typography>
            </Box>
            <KeyChip>{entry.defaultBinding}</KeyChip>
          </Stack>
        ))}
      </Stack>
    </SettingsSection>
  )
}
